import React, { FC, useState } from 'react';
import { useSelector } from 'react-redux';

import { IAbsenceRequestParams } from 'src/api/dtos';
import { AbsenceRequestCard } from 'src/features/absences/AbsenceRequestDrawer/AbsenceRequestCard';
import { Drawer, EmptyState } from 'src/shared/components';
import { useTranslate } from 'src/shared/providers';
import { getAbsencesDateRangesValue, getUniqueAbsenceTypes } from 'src/store/absence/absences.selectors';
import { RootState } from 'src/store/types';

import { AbsenceDetailsFilters } from './AbsenceDetailsFilters';

interface Props {
  open: boolean;
  onClose: () => void;
  cancelling: boolean;
  onCancelling: (notificationId: number) => void;
  onFiltersChange: (queryParams: IAbsenceRequestParams) => void;
}

export const AbsenceDetailsDrawer: FC<Props> = ({ open, cancelling, onClose, onCancelling, onFiltersChange }) => {
  const { t } = useTranslate();
  const { data, statuses } = useSelector((state: RootState) => state.absences);
  const uniqueTypes = useSelector(getUniqueAbsenceTypes);
  const dateRangeValue = useSelector(getAbsencesDateRangesValue);
  const [currentNotificationId, setCurrentNotificationId] = useState<number | undefined>();

  return (
    <Drawer
      open={open}
      title={t('absences.absenceDetails')}
      description={t('absences.youWillBeAbleToViewDetailsRegardingAllYourAbsencesHere')}
      onClose={onClose}
      overflow={false}
    >
      <AbsenceDetailsFilters types={uniqueTypes} statuses={statuses} onFiltersChange={onFiltersChange} />
      {!!dateRangeValue.length && (
        <div className="border-bottom d-flex justify-content-between align-items-center">
          <p className="text-muted small mb-1">{t('global.range')}</p>
          <p className="small mb-1">{dateRangeValue}</p>
        </div>
      )}
      {data.length > 0 ? (
        <div className="pt-3 overflow-auto" style={{ height: 'calc(100% - 100px)' }}>
          {data.map((absence) => (
            <AbsenceRequestCard
              key={absence.absenceRequestId}
              className="mb-3"
              absence={absence}
              cancelling={cancelling && currentNotificationId === absence.notificationId}
              onCancelling={(notificationId) => {
                setCurrentNotificationId(notificationId);
                onCancelling(notificationId);
              }}
            />
          ))}
        </div>
      ) : (
        <EmptyState title={t('absences.noAbsencesYet')} message={t('absences.youDoNotHaveAnyPastOrUpcomingAbsences')} />
      )}
    </Drawer>
  );
};
